import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'


/** API */
import AccessChannelApi from 'api/setting/default-users/access-channel.api'

/** UTILS || SLICE */
import { resetModal, submitModal, unSubmitModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import HeaderCard from 'component/CardCustom/HeaderCard'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import { TRUE_FALSE } from 'constants/status'
import CheckboxButton from 'new-components/buttons/checkbox-button'
import MainButton from 'new-components/buttons/main-button'
import SwitchButton from 'new-components/buttons/switch-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import PageModal from 'new-components/modal/page-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

const initStateErrorMessage = {
  ACCESS_CHANNEL_NAME: ``,
  IS_DUPLICATE_ACCESS_CHANNEL_NAME: ``,
  ACCESS_CHANNEL_NAME_STRING_BASE: ``,
  ACCESS_CHANNEL_NAME_STRING_EMPTY: ``,
  ACCESS_CHANNEL_NAME_MAXIMUM_LENGTH: ``,
  ACCESS_CHANNEL_NAME_ANY_REQUIRED: ``,

  ACCESS_CHANNEL_NAME_EN: ``,
  IS_DUPLICATE_ACCESS_CHANNEL_NAME_EN: ``,
  ACCESS_CHANNEL_NAME_EN_STRING_BASE: ``,
  ACCESS_CHANNEL_NAME_EN_STRING_EMPTY: ``,
  ACCESS_CHANNEL_NAME_EN_MAXIMUM_LENGTH: ``,
  ACCESS_CHANNEL_NAME_EN_ANY_REQUIRED: ``
}
export default function AccessChannels() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [accessChannels, setChannels] = useState([])
  const [accessChannelId, setAccessChannelId] = useState(Number)
  const [channelName, setChannelName] = useState('')
  const [isOther, setIsOther] = useState<TRUE_FALSE>(TRUE_FALSE.FALSE)
  const [accessChannelStatus, setChannelStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const [disabledSubmit, setDisabledSubmit] = useState(false)
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const [isPopup, setIsPopup] = useState(false)


  const loadData = async () => {
    try {
      const resp = await AccessChannelApi.findAll({
        page: page,
        pageLimit: pageLimit,
        search: search,
        sortBy: sortBy,
        sortType: sortType,
        status: status === 'ALL' ? '' : status
      })
      if (resp.status === 200) {
        setRowCount(resp.headers['x-total'])
        setChannels(resp.data)
      } else {
        setChannels([])
      }
    } catch (error) {
      notiError('ERROR')
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])

  const submit = () => {
    if (!channelName) return setErrorMessage({ ...errorMessage, ...{ ACCESS_CHANNEL_NAME: t('ACCESS_CHANNEL.MESSAGE.ACCESS_CHANNEL_NAME') } })

    dispatch(submitModal())
    setDisabledSubmit(true)
    AccessChannelApi.create(channelName, isOther)
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t('ACCESS_CHANNEL.MESSAGE.SUCCESS.CREATE'), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
          setDisabledSubmit(false)
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`ACCESS_CHANNEL.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
          setDisabledSubmit(false)
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`ACCESS_CHANNEL.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
        setDisabledSubmit(false)
      }).finally(() => setDisabledSubmit(false))
  }

  const submitEdit = () => {
    if (!channelName) return setErrorMessage({ ...errorMessage, ...{ ACCESS_CHANNEL_NAME: t(`ACCESS_CHANNEL.MESSAGE.ACCESS_CHANNEL_NAME`) } })

    dispatch(submitModal())
    setDisabledSubmit(true)
    AccessChannelApi.update(accessChannelId, {
      channelName,
      isOther,
      status: accessChannelStatus
    })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`ACCESS_CHANNEL.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`ACCESS_CHANNEL.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`ACCESS_CHANNEL.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      }).finally(() => setDisabledSubmit(false))
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setAccessChannelId(0)
    setChannelName('')
    setChannelStatus('')
    setIsOther(TRUE_FALSE.FALSE)
    setIsPopup(false)
    clearErrorMessage()
  }

  const onActive = () => {
    if (id) {
      AccessChannelApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('ACCESS_CHANNEL.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('ACCESS_CHANNEL.MESSAGE.ERROR'))
        }
      })
    }
  }
  const onDelete = () => {
    if (id) {
      AccessChannelApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('ACCESS_CHANNEL.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('ACCESS_CHANNEL.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { accessChannelId: acId, channelName: acName, isOther, status: acStatus } = data
    setAccessChannelId(acId)
    setChannelName(acName)
    setIsOther(isOther)
    setChannelStatus(acStatus)
    setIsPopup(true)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }
  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }
  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'channelName', disablePadding: false, label: t('ACCESS_CHANNEL.TABLE.CELL.ACCESS_CHANNEL_NAME') },
    { id: 'status', disablePadding: false, label: t('ACCESS_CHANNEL.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('ACCESS_CHANNEL.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { accessChannelId: acId, channelName: acName, updatedBy, updatedAt, status: acStatus } = objData
    const statusBtnActive = acStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = acStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: acId,
      id: acId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: acName },
        { option: 'STATUS', align: 'center', label: acStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.channelName)
  }
  return (
    <Box>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`ACCESS_CHANNEL.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`ACCESS_CHANNEL.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { accessChannelName: name, status: t(`ACCESS_CHANNEL.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('ACCESS_CHANNEL.TITLE')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={12} md={3} xl={2}>
            <TextInput
              label={t('ACCESS_CHANNEL.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} xl={8}>
            <Box display={'flex'} justifyContent={'space-between'}>
              <MainButton
                title={t('INPUT.SEARCH')}
                type='primary'
                variant='contained'
                style={{ width: '120px' }}
                onClick={() => {
                  setPage(1)
                  loadData()
                }} />
              <MainButton
                title={t('ACCESS_CHANNEL.BUTTON.ADD')}
                disabled={permissions.isCreate.disabled}
                type='primary'
                variant='contained'
                onClick={() => setIsPopup(true)}
                startIcon={<AddCircleIcon />}
              />
            </Box>
          </Grid>
        </Grid>
        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={accessChannels.map((val, i) => {
              return renderData(val, i)
            })}
          />
        </Box>
        <PageModal
          title={accessChannelId ? t('ACCESS_CHANNEL.TITLE_UPDATE') : t('ACCESS_CHANNEL.TITLE_CREATE')}
          isOpen={isPopup}
          onCancel={{ onClick: () => resetForm() }}
          onSubmit={{ onClick: () => accessChannelId ? submitEdit() : submit(), disabled: disabledSubmit }}
          handleClose={resetForm}
          size={'XS'}
        >
          <Box py={2}>
            {accessChannelId ? (
              <Box display="flex" justifyContent="space-between" alignItems="center" pb={1}>
                <Typography>{t('INPUT.LABEL_STATUS')}</Typography>
                <SwitchButton
                  onChange={() => setChannelStatus(accessChannelStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                  checked={accessChannelStatus === 'ACTIVE'}
                />
              </Box>
            ) : null}

            <Box display="flex" flexDirection="column" gap={2}>
              <TextInput
                fullWidth
                onChange={(event) => {
                  setChannelName(event.target.value)
                  clearErrorMessage()
                }}
                value={channelName}
                label={t('ACCESS_CHANNEL.INPUT.ACCESS_CHANNEL_NAME')}
                helperText={
                  errorMessage.ACCESS_CHANNEL_NAME ||
                  errorMessage.IS_DUPLICATE_ACCESS_CHANNEL_NAME ||
                  errorMessage.ACCESS_CHANNEL_NAME_STRING_BASE ||
                  errorMessage.ACCESS_CHANNEL_NAME_STRING_EMPTY ||
                  errorMessage.ACCESS_CHANNEL_NAME_MAXIMUM_LENGTH ||
                  errorMessage.ACCESS_CHANNEL_NAME_ANY_REQUIRED
                }
                required
              />
              <Box px={1.5}>
                <CheckboxButton
                  key="checkbox-input"
                  checked={isOther === TRUE_FALSE.TRUE}
                  label={t('ACCESS_CHANNEL.INPUT.SPECIFIED')}
                  onChange={(e) => {
                    setIsOther(e?.target?.checked ? TRUE_FALSE.TRUE : TRUE_FALSE.FALSE)
                  }}
                />
              </Box>
            </Box>
          </Box>
        </PageModal>
      </Box>

    </Box>
  )
}
